import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { C } from '../tokens';
import Icon from '../components/Icon';
import { Card, SectionTitle } from '../components/UI';
import useBreakpoint from '../useBreakpoint';
import '../APIHubPage.css';

/* ═══════════════════════════════════════════════════════════
   Capability options for Generate API Key modal
   ═══════════════════════════════════════════════════════════ */
const KEY_CAPABILITIES = [
  'Invoice Extraction', 'Customer Identity', 'Fraud Detection',
  'KYC Suite', 'Bank Analysis', 'Resume Parser',
  'Legal Discovery', 'Healthcare ICD-10', 'Supply Chain Docs',
  'Receipt OCR', 'Custom Classifier',
];

/* ═══════════════════════════════════════════════════════════
   Data — 11 API Services with per-service sample outputs
   ═══════════════════════════════════════════════════════════ */
const API_SERVICES = [
  {
    id: 'identity', icon: 'id_card', label: 'Customer Identity Verification',
    desc: 'Official ID verification',
    prompt: 'Extract and verify identity fields from the uploaded ID document.',
    sampleJSON: `{
  "status": "success",
  "document_id": "id_v8k_201",
  "verification": {
    "full_name": "Budi Santoso",
    "id_number": "3201041508900003",
    "date_of_birth": "1990-08-15",
    "nationality": "Indonesian",
    "document_type": "KTP",
    "expiry_date": "2030-08-15",
    "face_match_score": 0.97,
    "liveness_check": true
  },
  "confidence_score": 0.9914
}`,
  },
  {
    id: 'fraud', icon: 'shield_person', label: 'Document Fraud Detection',
    desc: 'Real-time risk scoring',
    prompt: 'Analyze document for signs of forgery, tampering, or inconsistency.',
    sampleJSON: `{
  "status": "success",
  "document_id": "fraud_x91_k3",
  "analysis": {
    "risk_level": "LOW",
    "risk_score": 0.08,
    "checks_passed": 12,
    "checks_failed": 0,
    "tampering_detected": false,
    "font_consistency": true,
    "metadata_integrity": true,
    "digital_signature_valid": true
  },
  "confidence_score": 0.9967
}`,
  },
  {
    id: 'invoice', icon: 'receipt_long', label: 'Invoice Extraction',
    desc: 'Fields, tables, and tax lines',
    prompt: 'Extract invoice number, vendor, amounts, line items, and tax details.',
    sampleJSON: `{
  "status": "success",
  "document_id": "nier_99x_z21",
  "extraction": {
    "invoice_number": "INV-9902",
    "vendor": {
      "name": "Nieramind Solutions Corp",
      "tax_id": "US-9912001",
      "address": "1200 Silicon Ave, CA"
    },
    "amounts": {
      "subtotal": 12500.00,
      "tax": 1000.00,
      "total": 13500.00,
      "currency": "USD"
    },
    "line_items": [
      { "description": "API Usage Fee", "qty": 1, "price": 12500.00 }
    ]
  },
  "confidence_score": 0.9982
}`,
  },
  {
    id: 'kyc', icon: 'person_check', label: 'KYC Data Extraction',
    desc: 'Full compliance suite',
    prompt: 'Extract and validate KYC data including PEP screening and AML checks.',
    sampleJSON: `{
  "status": "success",
  "document_id": "kyc_m3r_88",
  "kyc_result": {
    "customer_name": "Siti Aminah",
    "risk_category": "MEDIUM",
    "pep_screening": "CLEAR",
    "sanctions_check": "CLEAR",
    "aml_flag": false,
    "source_of_funds": "Employment",
    "verification_level": "ENHANCED"
  },
  "confidence_score": 0.9845
}`,
  },
  {
    id: 'bank', icon: 'account_balance', label: 'Bank Statement Analysis',
    desc: 'Statement & txn parser',
    prompt: 'Parse bank statement for transactions, balances, and spending patterns.',
    sampleJSON: `{
  "status": "success",
  "document_id": "bank_p2w_55",
  "analysis": {
    "account_holder": "Reza Pratama",
    "bank_name": "Bank Central Asia",
    "period": "2024-01-01 to 2024-01-31",
    "opening_balance": 45000000,
    "closing_balance": 52300000,
    "total_credits": 18500000,
    "total_debits": 11200000,
    "transaction_count": 47,
    "currency": "IDR"
  },
  "confidence_score": 0.9921
}`,
  },
  {
    id: 'resume', icon: 'work', label: 'Resume Parser',
    desc: 'CV & profile analytics',
    prompt: 'Extract structured resume data including skills, experience, and education.',
    sampleJSON: `{
  "status": "success",
  "document_id": "res_j7t_19",
  "parsed": {
    "name": "Dian Wijaya",
    "email": "dian.w@email.com",
    "phone": "+62-812-XXXX-XXXX",
    "skills": ["React", "Node.js", "Python", "AWS"],
    "experience_years": 6,
    "latest_role": "Senior Engineer at TechCorp",
    "education": "B.Sc Computer Science, UI"
  },
  "confidence_score": 0.9876
}`,
  },
  {
    id: 'qr', icon: 'qr_code_scanner', label: 'QR Code Reader',
    desc: 'Decode embedded QR data',
    prompt: 'Detect and decode all QR codes and barcodes in the uploaded image.',
    sampleJSON: `{
  "status": "success",
  "document_id": "qr_n4x_07",
  "decoded": {
    "codes_found": 2,
    "results": [
      {
        "type": "QR_CODE",
        "data": "https://pay.nieramind.ai/inv/9902",
        "position": { "x": 120, "y": 84 }
      },
      {
        "type": "BARCODE_128",
        "data": "INV-9902-2024",
        "position": { "x": 310, "y": 520 }
      }
    ]
  },
  "confidence_score": 0.9995
}`,
  },
  {
    id: 'signature', icon: 'draw', label: 'Signature Detection',
    desc: 'Locate & verify signatures',
    prompt: 'Detect signature regions, verify authenticity, and compare against reference.',
    sampleJSON: `{
  "status": "success",
  "document_id": "sig_q2k_44",
  "detection": {
    "signatures_found": 1,
    "regions": [
      {
        "page": 1,
        "bbox": [342, 680, 510, 730],
        "confidence": 0.96
      }
    ],
    "match_score": 0.91,
    "forged_probability": 0.03
  },
  "confidence_score": 0.9889
}`,
  },
  {
    id: 'audio', icon: 'graphic_eq', label: 'Audio Analysis',
    desc: 'Transcription & sentiment',
    prompt: 'Transcribe audio, detect speakers, and analyze sentiment and keywords.',
    sampleJSON: `{
  "status": "success",
  "document_id": "aud_v5m_63",
  "analysis": {
    "duration_seconds": 184,
    "speakers_detected": 2,
    "language": "id-ID",
    "transcript_preview": "Selamat pagi, saya ingin bertanya...",
    "sentiment": "NEUTRAL",
    "keywords": ["pricing", "enterprise", "demo"]
  },
  "confidence_score": 0.9734
}`,
  },
  {
    id: 'data', icon: 'table_chart', label: 'Data Extraction',
    desc: 'Tables & structured fields',
    prompt: 'Extract all tables, key-value pairs, and structured data from the document.',
    sampleJSON: `{
  "status": "success",
  "document_id": "dat_r8n_32",
  "extraction": {
    "tables_found": 3,
    "key_value_pairs": 18,
    "tables": [
      {
        "id": "T1",
        "rows": 12,
        "cols": 5,
        "header": ["Item", "Qty", "Unit", "Price", "Total"]
      }
    ],
    "fields": {
      "document_date": "2024-03-15",
      "reference_number": "REF-2024-0891"
    }
  },
  "confidence_score": 0.9903
}`,
  },
  {
    id: 'payslip', icon: 'payments', label: 'Payslip Extraction',
    desc: 'Salary & deduction parsing',
    prompt: 'Extract employee details, earnings, deductions, and net pay from payslip.',
    sampleJSON: `{
  "status": "success",
  "document_id": "pay_w6j_81",
  "extraction": {
    "employee_name": "Lina Tan",
    "employee_id": "EMP-00421",
    "period": "January 2024",
    "basic_salary": 15000000,
    "allowances": 3500000,
    "deductions": {
      "tax": 1850000,
      "insurance": 450000
    },
    "net_pay": 16200000,
    "currency": "IDR"
  },
  "confidence_score": 0.9958
}`,
  },
];

const MODELS = [
  'Nieramind-GPT-4v (Latest)',
  'Nieramind-Extraction-v2',
  'Base-OCR-Tesseract-Pro',
];

/* ═══════════════════════════════════════════════════════════
   Toggle Switch
   ═══════════════════════════════════════════════════════════ */
function Toggle({ on, onToggle }) {
  return (
    <button onClick={onToggle} aria-label="Toggle" style={{
      position: 'relative', width: 40, height: 22, borderRadius: 99,
      background: on ? C.primary : '#d6d3d1', border: 'none', cursor: 'pointer',
      transition: 'background .2s', flexShrink: 0, padding: 0,
    }}>
      <span style={{
        position: 'absolute', top: 2, left: on ? 20 : 2,
        width: 18, height: 18, borderRadius: '50%', background: '#fff',
        transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.18)',
      }} />
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════
   Range Slider
   ═══════════════════════════════════════════════════════════ */
function Slider({ label, value, min, max, step, onChange, hint }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <label style={{ fontSize: 10, fontWeight: 800, color: C.t3, textTransform: 'uppercase', letterSpacing: '.08em' }}>{label}</label>
        <span style={{ fontSize: 12, fontWeight: 800, color: C.primary }}>{value}</span>
      </div>
      <input type="range" className="apihub-slider" min={min} max={max} step={step}
        value={value} onChange={e => onChange(parseFloat(e.target.value))}
        style={{ width: '100%', height: 4 }} />
      {hint && <p style={{ fontSize: 9, color: C.t3, marginTop: 6, fontStyle: 'italic', lineHeight: 1.4 }}>{hint}</p>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Multi-Carousel Component
   Custom implementation inspired by react-multi-carousel:
   - Responsive breakpoints (items per view)
   - Prev / Next arrow navigation
   - Dot indicators
   - Touch swipe on mobile
   - Smooth CSS transform transitions
   ═══════════════════════════════════════════════════════════ */
function MultiCarousel({ items, renderItem, itemGap, mob, isTablet, isWide }) {
  const gap = itemGap || (mob ? 10 : 12);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchDelta, setTouchDelta] = useState(0);
  const [dragging, setDragging] = useState(false);

  // Responsive visible count (mirrors react-multi-carousel breakpoints)
  const visibleCount = mob ? 2 : isTablet ? 3 : isWide ? 6 : 4;
  const maxIndex = Math.max(0, items.length - visibleCount);

  // Clamp when breakpoint changes
  useEffect(() => {
    setCurrentIndex(prev => Math.min(prev, maxIndex));
  }, [maxIndex]);

  const goTo = useCallback((idx) => {
    setCurrentIndex(Math.max(0, Math.min(idx, maxIndex)));
  }, [maxIndex]);

  const goPrev = () => goTo(currentIndex - 1);
  const goNext = () => goTo(currentIndex + 1);

  // Touch handlers for swipe support
  const onTouchStart = (e) => { setTouchStartX(e.touches[0].clientX); setDragging(true); };
  const onTouchMove = (e) => { if (touchStartX !== null) setTouchDelta(e.touches[0].clientX - touchStartX); };
  const onTouchEnd = () => {
    if (Math.abs(touchDelta) > 40) { touchDelta < 0 ? goNext() : goPrev(); }
    setTouchStartX(null); setTouchDelta(0); setDragging(false);
  };

  // Layout math
  const itemPct = 100 / visibleCount;
  const gapShrink = gap * (visibleCount - 1) / visibleCount;
  const tx = -(currentIndex * itemPct);
  const canPrev = currentIndex > 0;
  const canNext = currentIndex < maxIndex;

  const arrowBtn = (enabled) => ({
    width: 32, height: 32, borderRadius: '50%', border: `1px solid ${C.bdr}`,
    background: enabled ? C.w : '#f5f3f1', cursor: enabled ? 'pointer' : 'default',
    display: 'grid', placeItems: 'center', flexShrink: 0,
    opacity: enabled ? 1 : 0.35, transition: 'all .15s',
    boxShadow: enabled ? '0 2px 8px rgba(0,0,0,.06)' : 'none',
  });

  return (
    <div>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <h3 style={{ fontSize: 12, fontWeight: 800, color: C.t1, textTransform: 'uppercase', letterSpacing: '.08em' }}>
          Select API Service
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, color: C.t3, fontWeight: 500 }}>{items.length} Models Available</span>
          <button onClick={goPrev} disabled={!canPrev} aria-label="Previous" style={arrowBtn(canPrev)}>
            <Icon name="chevron_left" size={18} style={{ color: canPrev ? C.t1 : C.t3 }} />
          </button>
          <button onClick={goNext} disabled={!canNext} aria-label="Next" style={arrowBtn(canNext)}>
            <Icon name="chevron_right" size={18} style={{ color: canNext ? C.t1 : C.t3 }} />
          </button>
        </div>
      </div>

      {/* Track */}
      <div style={{ overflow: 'hidden', borderRadius: 14 }}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
      >
        <div style={{
          display: 'flex', gap: gap,
          transform: `translateX(calc(${tx}% - ${currentIndex * gap}px + ${dragging ? touchDelta : 0}px))`,
          transition: dragging ? 'none' : 'transform .35s cubic-bezier(.4,0,.2,1)',
        }}>
          {items.map((item, i) => (
            <div key={item.id} style={{ flex: `0 0 calc(${itemPct}% - ${gapShrink}px)`, minWidth: 0 }}>
              {renderItem(item, i)}
            </div>
          ))}
        </div>
      </div>

      {/* Dots */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 12 }}>
        {Array.from({ length: maxIndex + 1 }, (_, i) => (
          <button key={i} onClick={() => goTo(i)} aria-label={`Slide group ${i + 1}`} style={{
            width: currentIndex === i ? 18 : 6, height: 6, borderRadius: 99,
            background: currentIndex === i ? C.primary : '#d6d3d1',
            border: 'none', cursor: 'pointer', padding: 0, transition: 'all .25s',
          }} />
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Configuration Panel
   ═══════════════════════════════════════════════════════════ */
function ConfigPanel({ config, setConfig, onReset, mob }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Config Header */}
      <div style={{ padding: mob ? '16px 16px 14px' : '24px 24px 20px', borderBottom: `1px solid ${C.bdr}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: mob ? 12 : 18 }}>
          <Icon name="tune" size={18} style={{ color: C.primary }} />
          <h3 style={{ fontSize: 12, fontWeight: 800, color: C.t1, textTransform: 'uppercase', letterSpacing: '.1em' }}>Configuration</h3>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: mob ? 18 : 22 }}>
          {/* Model Selector */}
          <div>
            <label style={{ display: 'block', fontSize: 10, fontWeight: 800, color: C.t3, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>Primary Model</label>
            <select value={config.model} onChange={e => setConfig(p => ({ ...p, model: e.target.value }))}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 10, fontSize: 13, fontWeight: 600, border: `1px solid ${C.bdr}`, background: C.w, outline: 'none', color: C.t1, boxSizing: 'border-box' }}>
              {MODELS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          {/* Sliders */}
          <Slider label="Temperature" value={config.temperature} min={0} max={1} step={0.1}
            onChange={v => setConfig(p => ({ ...p, temperature: v }))}
            hint="Higher values make the output more creative and varied." />
          <Slider label="Similarity" value={config.similarity} min={0} max={1} step={0.05}
            onChange={v => setConfig(p => ({ ...p, similarity: v }))} />
          <Slider label="Chunk Size" value={config.chunkSize} min={128} max={2048} step={128}
            onChange={v => setConfig(p => ({ ...p, chunkSize: v }))} />
          <div style={{ height: 1, background: C.bdr }} />

          {/* Toggles */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.t1 }}>Safety Rules</div>
                <div style={{ fontSize: 10, color: C.t3 }}>Block sensitive content</div>
              </div>
              <Toggle on={config.safety} onToggle={() => setConfig(p => ({ ...p, safety: !p.safety }))} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.t1 }}>PII Masking</div>
                <div style={{ fontSize: 10, color: C.t3 }}>Auto-redact personal data</div>
              </div>
              <Toggle on={config.pii} onToggle={() => setConfig(p => ({ ...p, pii: !p.pii }))} />
            </div>
          </div>
        </div>
      </div>
      <div style={{ flex: 1 }} />

      {/* Footer */}
      <div style={{ padding: mob ? 16 : 24, background: '#faf9f8', borderTop: `1px solid ${C.bdr}` }}>
        <button onClick={onReset} style={{
          width: '100%', padding: '12px 0', borderRadius: 10, border: 'none',
          background: C.primary, color: '#fff', fontSize: 13, fontWeight: 700,
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          boxShadow: '0 4px 14px rgba(236,91,19,.2)',
        }}>
          <Icon name="settings_backup_restore" size={16} /> Reset Config
        </button>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 12 }}>
          <Icon name="info" size={12} style={{ color: C.t3 }} />
          <span style={{ fontSize: 10, color: C.t3 }}>Configuration is applied globally to current session.</span>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Main Page
   ═══════════════════════════════════════════════════════════ */
export default function APIHubPage() {
  const { isMobile, isTablet, isDesktop, isWide } = useBreakpoint();
  const mob = isMobile;
  const compact = isMobile || isTablet;
  const pad = mob ? 16 : isTablet ? 24 : 32;

  const [selectedApi, setSelectedApi] = useState('invoice');
  const [query, setQuery] = useState('');
  const [copied, setCopied] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [config, setConfig] = useState({
    model: MODELS[0], temperature: 0.7, similarity: 0.85,
    chunkSize: 512, safety: true, pii: false,
  });
  const fileRef = useRef(null);
  const navigate = useNavigate();

  // ── Generate API Key modal ──
  const [keyModalOpen, setKeyModalOpen] = useState(false);
  const [keyForm, setKeyForm] = useState({ name: '', env: 'production', caps: ['Invoice Extraction', 'Customer Identity'] });
  const [generatedKey, setGeneratedKey] = useState(null);
  const [keyCopied, setKeyCopied] = useState(false);

  const toggleCap = (cap) => setKeyForm(p => ({
    ...p, caps: p.caps.includes(cap) ? p.caps.filter(c => c !== cap) : [...p.caps, cap],
  }));
  const selectAllCaps = () => setKeyForm(p => ({
    ...p, caps: p.caps.length === KEY_CAPABILITIES.length ? [] : [...KEY_CAPABILITIES],
  }));
  const handleGenerateKey = () => {
    if (!keyForm.name.trim()) return;
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    const rand = Array.from({ length: 32 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    setGeneratedKey('niera_' + keyForm.env.slice(0, 4) + '_' + rand);
  };
  const handleCopyKey = () => {
    navigator.clipboard?.writeText(generatedKey).catch(() => {});
    setKeyCopied(true);
    setTimeout(() => setKeyCopied(false), 2000);
  };
  const resetKeyModal = () => {
    setKeyModalOpen(false); setGeneratedKey(null); setKeyCopied(false);
    setKeyForm({ name: '', env: 'production', caps: ['Invoice Extraction', 'Customer Identity'] });
  };

  // ── File state ──
  const [uploadedFile, setUploadedFile] = useState(null);     // { name, size, type, url, category }
  const [dragActive, setDragActive] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [analyzed, setAnalyzed] = useState(false);

  // Active service drives JSON output, prompt placeholder, and context banner
  const activeService = API_SERVICES.find(s => s.id === selectedApi) || API_SERVICES[0];

  const resetConfig = () => setConfig({
    model: MODELS[0], temperature: 0.7, similarity: 0.85,
    chunkSize: 512, safety: true, pii: false,
  });

  // ── File helpers ──
  const getFileCategory = (file) => {
    const t = file.type || '';
    const ext = file.name.split('.').pop().toLowerCase();
    if (t === 'application/pdf' || ext === 'pdf') return 'pdf';
    if (t.startsWith('image/') || ['jpg','jpeg','png','tiff','tif','webp','gif'].includes(ext)) return 'image';
    if (t.includes('wordprocessingml') || t.includes('msword') || ['doc','docx'].includes(ext)) return 'word';
    if (t.startsWith('audio/') || ['mp3','wav','ogg','m4a'].includes(ext)) return 'audio';
    return 'other';
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const processFile = (file) => {
    if (!file) return;
    // 25MB limit
    if (file.size > 25 * 1024 * 1024) { alert('File size exceeds 25MB limit.'); return; }
    const cat = getFileCategory(file);
    const url = URL.createObjectURL(file);
    setUploadedFile({ name: file.name, size: file.size, type: file.type, url, category: cat });
    setZoom(1);
    setAnalyzed(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const handleRemoveFile = (e) => {
    e?.stopPropagation();
    if (uploadedFile?.url) URL.revokeObjectURL(uploadedFile.url);
    setUploadedFile(null);
    setZoom(1);
    setAnalyzed(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => { if (uploadedFile?.url) URL.revokeObjectURL(uploadedFile.url); };
  }, [uploadedFile]);

  // ── Drag & drop handlers ──
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  // ── Zoom ──
  const zoomIn = (e) => { e.stopPropagation(); setZoom(z => Math.min(z + 0.25, 3)); };
  const zoomOut = (e) => { e.stopPropagation(); setZoom(z => Math.max(z - 0.25, 0.5)); };
  const zoomReset = (e) => { e.stopPropagation(); setZoom(1); };

  // ── Copy / Download ──
  const handleCopy = () => {
    navigator.clipboard?.writeText(activeService.sampleJSON).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadJSON = () => {
    const blob = new Blob([activeService.sampleJSON], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${selectedApi}_result.json`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  // ── Analyze flow ──
  const handleAnalyze = () => {
    if (!uploadedFile) { alert('Please upload a document first.'); return; }
    setAnalyzing(true);
    setAnalyzed(false);
    setTimeout(() => { setAnalyzing(false); setAnalyzed(true); }, 1800);
  };

  /* Carousel card renderer */
  const renderServiceCard = (s) => {
    const active = selectedApi === s.id;
    return (
      <div onClick={() => setSelectedApi(s.id)} style={{
        position: 'relative', display: 'flex', flexDirection: 'column', gap: 10,
        padding: mob ? '14px 12px' : '16px 14px', height: '100%', boxSizing: 'border-box',
        background: C.w,
        border: `${active ? 2 : 1}px solid ${active ? C.primary : C.bdr}`,
        borderRadius: 14, cursor: 'pointer',
        boxShadow: active ? `0 0 0 3px ${C.pL}` : 'none',
        transition: 'all .15s',
      }}
        onMouseEnter={e => { if (!active) e.currentTarget.style.borderColor = `${C.primary}60`; }}
        onMouseLeave={e => { if (!active) e.currentTarget.style.borderColor = active ? C.primary : C.bdr; }}
      >
        <div style={{
          width: mob ? 36 : 40, height: mob ? 36 : 40, borderRadius: 10,
          background: active ? C.pL : '#f5f3f1',
          display: 'grid', placeItems: 'center',
          color: active ? C.primary : C.t3, transition: 'all .15s',
        }}>
          <Icon name={s.icon} size={mob ? 18 : 20} />
        </div>
        <div>
          <h4 style={{ fontSize: mob ? 12 : 13, fontWeight: 700, color: C.t1, lineHeight: 1.3 }}>{s.label}</h4>
          <p style={{ fontSize: 10, color: C.t3, lineHeight: 1.4, marginTop: 2 }}>{s.desc}</p>
        </div>
        {active && (
          <div style={{ position: 'absolute', top: 8, right: 8 }}>
            <Icon name="check_circle" size={16} style={{ color: C.primary }} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ height: '100%', overflow: 'auto' }}>
      <div className="apihub-layout" style={{ display: 'flex' }}>

        {/* ═════ Main Content ═════ */}
        <div className="apihub-main" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>

          {/* Header */}
          <div style={{
            background: C.w, borderBottom: `1px solid ${C.bdr}`,
            padding: mob ? '20px 16px' : '28px 32px',
            display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between',
            alignItems: mob ? 'flex-start' : 'flex-end', gap: mob ? 14 : 16,
          }}>
            <div>
              {!mob && (
                <div style={{ fontSize: 10, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 6 }}>
                  CONSOLE &gt; API HUB
                </div>
              )}
              <h2 style={{ fontSize: mob ? 22 : 28, fontWeight: 900, letterSpacing: '-.02em', lineHeight: 1.1 }}>
                AI Capabilities Hub
              </h2>
              <p style={{ fontSize: 13, color: C.t2, marginTop: 4 }}>
                Test and configure our specialized document extraction and analysis APIs.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={() => navigate('/documentation')} style={{
                padding: '9px 18px', borderRadius: 10, border: `1px solid ${C.bdr}`,
                background: C.w, fontSize: 13, fontWeight: 700, cursor: 'pointer', color: C.t1,
              }}>View API Docs</button>
              <button onClick={() => setKeyModalOpen(true)} style={{
                padding: '9px 18px', borderRadius: 10, border: 'none',
                background: C.primary, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(236,91,19,.25)',
              }}>Get API Keys</button>
              {/* Mobile config toggle */}
              {compact && (
                <button onClick={() => setConfigOpen(true)} aria-label="Configuration" style={{
                  padding: '9px 14px', borderRadius: 10, border: `1px solid ${C.bdr}`,
                  background: C.w, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                  fontSize: 13, fontWeight: 700, color: C.t2,
                }}>
                  <Icon name="tune" size={18} style={{ color: C.primary }} />
                  {!mob && 'Config'}
                </button>
              )}
            </div>
          </div>

          {/* Scrollable body */}
          <div style={{ flex: 1, overflow: 'auto', padding: pad }}>

            {/* ═════ API Service Carousel ═════ */}
            <section style={{ marginBottom: mob ? 24 : 32 }}>
              <MultiCarousel
                items={API_SERVICES}
                renderItem={renderServiceCard}
                itemGap={mob ? 10 : 12}
                mob={mob}
                isTablet={isTablet}
                isWide={isWide}
              />
            </section>

            {/* ═════ Active service context banner ═════ */}
            <div style={{
              display: 'flex', alignItems: mob ? 'flex-start' : 'center', gap: 10,
              padding: mob ? '10px 14px' : '12px 16px',
              borderRadius: 10, marginBottom: mob ? 16 : 24,
              background: C.pL, border: `1px solid ${C.primary}20`,
            }}>
              <Icon name={activeService.icon} size={18} style={{ color: C.primary, flexShrink: 0, marginTop: mob ? 2 : 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: C.primary }}>{activeService.label}</span>
                {!mob && <span style={{ fontSize: 11, color: C.t2, marginLeft: 8 }}>— {activeService.prompt}</span>}
                {mob && <p style={{ fontSize: 11, color: C.t2, marginTop: 2, lineHeight: 1.4 }}>{activeService.prompt}</p>}
              </div>
            </div>

            {/* ═════ Semantic Preview & Test ═════ */}
            <section>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <Icon name="query_stats" size={22} style={{ color: C.teal }} />
                <h3 style={{ fontSize: mob ? 17 : 20, fontWeight: 800, color: C.t1 }}>Semantic Preview & Test</h3>
              </div>

              <div className="apihub-preview-grid" style={{
                display: 'grid',
                gridTemplateColumns: compact ? '1fr' : '1fr 1fr',
                gap: mob ? 16 : 24,
              }}>
                {/* ── Left: Upload + Query + Document Preview ── */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: mob ? 12 : 16 }}>

                  {/* File Upload Zone */}
                  <div
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                    onClick={() => { if (!uploadedFile) fileRef.current?.click(); }}
                    style={{
                      background: dragActive ? C.pL : C.w,
                      border: `2px dashed ${dragActive ? C.primary : uploadedFile ? C.green + '60' : C.bdr}`,
                      borderRadius: 16,
                      padding: uploadedFile ? (mob ? '14px' : '16px 20px') : (mob ? '28px 16px' : '36px 24px'),
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                      justifyContent: 'center', cursor: uploadedFile ? 'default' : 'pointer',
                      transition: 'all .2s',
                    }}
                    onMouseEnter={e => { if (!uploadedFile) e.currentTarget.style.borderColor = `${C.primary}50`; }}
                    onMouseLeave={e => { if (!uploadedFile) e.currentTarget.style.borderColor = C.bdr; }}
                  >
                    <input ref={fileRef} type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.tiff,.tif,.mp3,.wav,.ogg"
                      onChange={handleFileChange}
                      style={{ display: 'none' }} />

                    {!uploadedFile ? (
                      <>
                        <div style={{
                          width: 48, height: 48, borderRadius: '50%',
                          background: dragActive ? `${C.primary}18` : '#f5f3f1',
                          display: 'grid', placeItems: 'center', marginBottom: 10,
                          transition: 'background .2s',
                        }}>
                          <Icon name={dragActive ? 'file_download' : 'upload_file'} size={22}
                            style={{ color: dragActive ? C.primary : C.t3 }} />
                        </div>
                        <p style={{ fontSize: 13, fontWeight: 600, color: dragActive ? C.primary : C.t1 }}>
                          {dragActive ? 'Drop your file here' : 'Drag & drop your document here'}
                        </p>
                        <p style={{ fontSize: 11, color: C.t3, marginTop: 4 }}>Support PDF, Word, JPG, PNG, TIFF up to 25MB</p>
                        <button onClick={e => { e.stopPropagation(); fileRef.current?.click(); }} style={{
                          marginTop: 12, border: 'none', background: 'none', fontSize: 11,
                          fontWeight: 800, color: C.primary, cursor: 'pointer',
                          textTransform: 'uppercase', letterSpacing: '.06em',
                        }}>Browse local files</button>
                      </>
                    ) : (
                      /* ── Uploaded file info ── */
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                        <div style={{
                          width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                          background: uploadedFile.category === 'pdf' ? '#fee2e2'
                            : uploadedFile.category === 'image' ? '#dbeafe'
                            : uploadedFile.category === 'word' ? '#e0e7ff'
                            : uploadedFile.category === 'audio' ? '#fef3c7'
                            : '#f5f3f1',
                          display: 'grid', placeItems: 'center',
                        }}>
                          <Icon name={
                            uploadedFile.category === 'pdf' ? 'picture_as_pdf'
                            : uploadedFile.category === 'image' ? 'image'
                            : uploadedFile.category === 'word' ? 'description'
                            : uploadedFile.category === 'audio' ? 'audio_file'
                            : 'insert_drive_file'
                          } size={20} style={{
                            color: uploadedFile.category === 'pdf' ? C.red
                            : uploadedFile.category === 'image' ? '#3b82f6'
                            : uploadedFile.category === 'word' ? C.indigo
                            : uploadedFile.category === 'audio' ? C.amber
                            : C.t3,
                          }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: 13, fontWeight: 600, color: C.t1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {uploadedFile.name}
                          </p>
                          <p style={{ fontSize: 11, color: C.t3, marginTop: 2 }}>
                            {formatSize(uploadedFile.size)}
                            <span style={{ margin: '0 6px', color: C.bdr }}>•</span>
                            <span style={{ color: C.green, fontWeight: 600 }}>Ready to analyze</span>
                          </p>
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                          <button onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
                            aria-label="Replace file" style={{
                            width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.bdr}`,
                            background: C.w, cursor: 'pointer', display: 'grid', placeItems: 'center',
                          }}>
                            <Icon name="swap_horiz" size={16} style={{ color: C.t2 }} />
                          </button>
                          <button onClick={handleRemoveFile} aria-label="Remove file" style={{
                            width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.red}30`,
                            background: '#fef2f2', cursor: 'pointer', display: 'grid', placeItems: 'center',
                          }}>
                            <Icon name="close" size={16} style={{ color: C.red }} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Semantic Query — placeholder follows active service prompt */}
                  <div style={{
                    position: 'relative', background: C.w, borderRadius: 14, overflow: 'hidden',
                    border: `1px solid ${C.bdr}`, boxShadow: '0 1px 4px rgba(0,0,0,.04)',
                  }}>
                    <Icon name="auto_awesome" size={20} style={{
                      position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: C.teal,
                    }} />
                    <input type="text" placeholder={activeService.prompt}
                      value={query} onChange={e => setQuery(e.target.value)}
                      style={{
                        width: '100%', padding: mob ? '14px 110px 14px 42px' : '16px 130px 16px 46px',
                        border: 'none', fontSize: 13, fontWeight: 500, outline: 'none', color: C.t1,
                        boxSizing: 'border-box',
                      }} />
                    <button onClick={handleAnalyze} disabled={analyzing} style={{
                      position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                      padding: mob ? '8px 14px' : '9px 18px', borderRadius: 10, border: 'none',
                      background: analyzing ? C.t3 : !uploadedFile ? '#b0aca8' : C.teal,
                      color: '#fff', fontSize: 11, fontWeight: 800,
                      cursor: analyzing ? 'wait' : !uploadedFile ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', gap: 4,
                      textTransform: 'uppercase', transition: 'background .2s', opacity: !uploadedFile ? 0.7 : 1,
                    }}>
                      {analyzing ? 'Processing\u2026' : <> Analyze <Icon name="bolt" size={14} /></>}
                    </button>
                  </div>

                  {/* Document Preview — functional rendering per file type */}
                  <div style={{
                    background: '#e8e2df', borderRadius: 14,
                    minHeight: mob ? 240 : 340,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    position: 'relative', overflow: 'hidden',
                  }}>
                    {!uploadedFile ? (
                      /* ── Empty state ── */
                      <div style={{
                        width: '80%', height: mob ? 200 : 280, background: C.w, borderRadius: 6,
                        boxShadow: '0 8px 32px rgba(0,0,0,.12)', display: 'grid', placeItems: 'center',
                      }}>
                        <div style={{ textAlign: 'center' }}>
                          <Icon name="description" size={40} style={{ color: C.t3, opacity: 0.5 }} />
                          <p style={{ fontSize: 11, color: C.t3, marginTop: 8 }}>Upload a document to preview</p>
                        </div>
                      </div>
                    ) : uploadedFile.category === 'image' ? (
                      /* ── Image preview ── */
                      <div style={{
                        width: '100%', height: mob ? 280 : 380, overflow: 'auto',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <img src={uploadedFile.url} alt={uploadedFile.name} style={{
                          maxWidth: `${100 * zoom}%`, maxHeight: `${100 * zoom}%`,
                          objectFit: 'contain', transition: 'max-width .2s, max-height .2s',
                          borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,.15)',
                        }} />
                      </div>
                    ) : uploadedFile.category === 'pdf' ? (
                      /* ── PDF preview via embed ── */
                      <div style={{
                        width: '100%', height: mob ? 320 : 420,
                        display: 'flex', flexDirection: 'column',
                      }}>
                        <embed
                          src={uploadedFile.url + '#toolbar=0&view=FitH'}
                          type="application/pdf"
                          style={{
                            width: '100%', height: '100%', border: 'none', borderRadius: 6,
                            transform: `scale(${zoom})`, transformOrigin: 'top center',
                            transition: 'transform .2s',
                          }}
                        />
                      </div>
                    ) : uploadedFile.category === 'word' ? (
                      /* ── Word doc info card (no native browser preview) ── */
                      <div style={{
                        width: '80%', maxWidth: 320, background: C.w, borderRadius: 12,
                        boxShadow: '0 8px 32px rgba(0,0,0,.12)', padding: mob ? 20 : 28,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
                      }}>
                        <div style={{
                          width: 56, height: 56, borderRadius: 14, background: '#e0e7ff',
                          display: 'grid', placeItems: 'center', marginBottom: 12,
                        }}>
                          <Icon name="description" size={28} style={{ color: C.indigo }} />
                        </div>
                        <p style={{ fontSize: 14, fontWeight: 700, color: C.t1, wordBreak: 'break-word' }}>{uploadedFile.name}</p>
                        <p style={{ fontSize: 11, color: C.t3, marginTop: 4 }}>{formatSize(uploadedFile.size)}</p>
                        <p style={{ fontSize: 10, color: C.t2, marginTop: 8, lineHeight: 1.5 }}>
                          Word documents cannot be previewed in the browser. The file is ready to analyze.
                        </p>
                        <a href={uploadedFile.url} download={uploadedFile.name}
                          onClick={e => e.stopPropagation()}
                          style={{
                            marginTop: 12, fontSize: 11, fontWeight: 700, color: C.primary,
                            textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4,
                          }}>
                          <Icon name="download" size={14} /> Download to view
                        </a>
                      </div>
                    ) : uploadedFile.category === 'audio' ? (
                      /* ── Audio player ── */
                      <div style={{
                        width: '85%', maxWidth: 400, background: C.w, borderRadius: 12,
                        boxShadow: '0 8px 32px rgba(0,0,0,.12)', padding: mob ? 20 : 28,
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                      }}>
                        <div style={{
                          width: 56, height: 56, borderRadius: 14, background: '#fef3c7',
                          display: 'grid', placeItems: 'center', marginBottom: 12,
                        }}>
                          <Icon name="graphic_eq" size={28} style={{ color: C.amber }} />
                        </div>
                        <p style={{ fontSize: 13, fontWeight: 700, color: C.t1, wordBreak: 'break-word', textAlign: 'center' }}>{uploadedFile.name}</p>
                        <p style={{ fontSize: 11, color: C.t3, marginTop: 4 }}>{formatSize(uploadedFile.size)}</p>
                        <audio controls src={uploadedFile.url} style={{ width: '100%', marginTop: 14 }}>
                          Your browser does not support audio playback.
                        </audio>
                      </div>
                    ) : (
                      /* ── Other file type fallback ── */
                      <div style={{
                        width: '80%', maxWidth: 320, background: C.w, borderRadius: 12,
                        boxShadow: '0 8px 32px rgba(0,0,0,.12)', padding: mob ? 20 : 28,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
                      }}>
                        <div style={{
                          width: 56, height: 56, borderRadius: 14, background: '#f5f3f1',
                          display: 'grid', placeItems: 'center', marginBottom: 12,
                        }}>
                          <Icon name="insert_drive_file" size={28} style={{ color: C.t3 }} />
                        </div>
                        <p style={{ fontSize: 14, fontWeight: 700, color: C.t1, wordBreak: 'break-word' }}>{uploadedFile.name}</p>
                        <p style={{ fontSize: 11, color: C.t3, marginTop: 4 }}>{formatSize(uploadedFile.size)}</p>
                        <p style={{ fontSize: 10, color: C.t2, marginTop: 8 }}>Preview not available. File is ready to analyze.</p>
                      </div>
                    )}

                    {/* Bottom toolbar — always shown */}
                    <div style={{
                      position: 'absolute', bottom: 10, left: 10, right: 10,
                      background: 'rgba(255,255,255,.92)', backdropFilter: 'blur(6px)',
                      padding: '8px 12px', borderRadius: 10,
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}>
                      <span style={{
                        fontSize: 9, fontWeight: 800, color: C.t2, textTransform: 'uppercase',
                        letterSpacing: '.04em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        flex: 1, marginRight: 8,
                      }}>
                        {uploadedFile ? `Preview: ${uploadedFile.name}` : 'Preview: No file loaded'}
                      </span>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0 }}>
                        {uploadedFile && (uploadedFile.category === 'image' || uploadedFile.category === 'pdf') && (
                          <>
                            <button onClick={zoomOut} aria-label="Zoom out" style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 2 }}>
                              <Icon name="zoom_out" size={16} style={{ color: C.t2 }} />
                            </button>
                            <button onClick={zoomReset} aria-label="Reset zoom" style={{
                              border: 'none', background: 'none', cursor: 'pointer', padding: '1px 4px',
                              fontSize: 9, fontWeight: 700, color: C.t2,
                            }}>
                              {Math.round(zoom * 100)}%
                            </button>
                            <button onClick={zoomIn} aria-label="Zoom in" style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 2 }}>
                              <Icon name="zoom_in" size={16} style={{ color: C.t2 }} />
                            </button>
                          </>
                        )}
                        {!uploadedFile && (
                          <>
                            <button style={{ border: 'none', background: 'none', cursor: 'default', padding: 2, opacity: 0.35 }}>
                              <Icon name="zoom_in" size={16} style={{ color: C.t3 }} />
                            </button>
                            <button style={{ border: 'none', background: 'none', cursor: 'default', padding: 2, opacity: 0.35 }}>
                              <Icon name="zoom_out" size={16} style={{ color: C.t3 }} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Right: JSON Results Pane — stateful ── */}
                <div style={{
                  display: 'flex', flexDirection: 'column', background: '#1a1a1a', borderRadius: 14,
                  border: '1px solid #333', overflow: 'hidden', minHeight: mob ? 320 : 400,
                }}>
                  {/* JSON Header */}
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 16px', borderBottom: '1px solid #333', background: 'rgba(30,30,30,.6)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <div style={{
                        width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                        background: analyzed ? C.green : analyzing ? C.amber : '#555',
                        boxShadow: analyzing ? `0 0 6px ${C.amber}` : 'none',
                        animation: analyzing ? 'apihub-pulse 1s infinite' : 'none',
                      }} />
                      <span style={{
                        fontSize: 9, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase',
                        letterSpacing: '.1em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {analyzing ? 'Processing...' : analyzed ? `${activeService.label} Result (JSON)` : 'Extraction Result (JSON)'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                      <button onClick={handleCopy} disabled={!analyzed} style={{
                        border: 'none', background: 'none', fontSize: 10, fontWeight: 700,
                        color: !analyzed ? '#444' : copied ? C.green : '#6b7280',
                        cursor: analyzed ? 'pointer' : 'default',
                        display: 'flex', alignItems: 'center', gap: 4, transition: 'color .15s',
                      }}>
                        <Icon name={copied ? 'check' : 'content_copy'} size={14} /> {copied ? 'Copied!' : 'Copy'}
                      </button>
                      <button onClick={handleDownloadJSON} disabled={!analyzed} style={{
                        border: 'none', background: 'none', fontSize: 10, fontWeight: 700,
                        color: analyzed ? '#6b7280' : '#444', cursor: analyzed ? 'pointer' : 'default',
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}>
                        <Icon name="download" size={14} /> JSON
                      </button>
                    </div>
                  </div>

                  {/* JSON Body — state-driven */}
                  <div style={{
                    flex: 1, padding: mob ? 16 : 24, overflowY: 'auto', overflowX: 'auto',
                    fontFamily: "'Courier New', monospace", fontSize: mob ? 11 : 13,
                    lineHeight: 1.7, display: 'flex', flexDirection: 'column',
                  }}>
                    {analyzing ? (
                      /* ── Analyzing state ── */
                      <div style={{
                        flex: 1, display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center', gap: 14,
                      }}>
                        <div className="apihub-spinner" style={{
                          width: 36, height: 36, border: `3px solid #333`,
                          borderTopColor: C.primary, borderRadius: '50%',
                        }} />
                        <p style={{ fontSize: 12, color: '#6b7280', fontFamily: 'Public Sans, sans-serif', textAlign: 'center' }}>
                          Running <span style={{ color: C.primary, fontWeight: 700 }}>{activeService.label}</span>
                        </p>
                        <p style={{ fontSize: 10, color: '#555', fontFamily: 'Public Sans, sans-serif' }}>
                          Model: {config.model}
                        </p>
                      </div>
                    ) : analyzed ? (
                      /* ── Result JSON ── */
                      <pre style={{ color: '#34d399', whiteSpace: 'pre', margin: 0 }}>
                        {activeService.sampleJSON}
                      </pre>
                    ) : (
                      /* ── Idle / waiting state ── */
                      <div style={{
                        flex: 1, display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center', gap: 10,
                      }}>
                        <Icon name="data_object" size={36} style={{ color: '#444' }} />
                        <p style={{ fontSize: 12, color: '#6b7280', fontFamily: 'Public Sans, sans-serif', textAlign: 'center', lineHeight: 1.5 }}>
                          {uploadedFile
                            ? <>File loaded. Click <span style={{ color: C.teal, fontWeight: 700 }}>Analyze</span> to extract data.</>
                            : 'Upload a document and run analysis to see results here.'
                          }
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* ═════ Right Sidebar: Configuration (desktop only) ═════ */}
        {!compact && (
          <div className="apihub-config" style={{
            width: 290, flexShrink: 0, background: C.w,
            borderLeft: `1px solid ${C.bdr}`, display: 'flex', flexDirection: 'column',
          }}>
            <ConfigPanel config={config} setConfig={setConfig} onReset={resetConfig} mob={false} />
          </div>
        )}
      </div>

      {/* ═════ Mobile/Tablet Config Drawer ═════ */}
      {compact && configOpen && (
        <div onClick={() => setConfigOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(30,24,18,.45)',
          backdropFilter: 'blur(4px)', zIndex: 9999,
          display: 'grid', placeItems: mob ? 'end center' : 'center',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: C.w, width: '100%', maxWidth: mob ? '100%' : 420,
            maxHeight: mob ? '90vh' : '80vh', borderRadius: mob ? '18px 18px 0 0' : 18,
            overflowY: 'auto', display: 'flex', flexDirection: 'column',
            boxShadow: '0 -8px 40px rgba(0,0,0,.15)',
          }}>
            {/* Drawer header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 16px', borderBottom: `1px solid ${C.bdr}`, position: 'sticky', top: 0,
              background: C.w, zIndex: 1,
            }}>
              <span style={{ fontSize: 14, fontWeight: 700 }}>Configuration</span>
              <button onClick={() => setConfigOpen(false)} aria-label="Close" style={{
                border: 'none', background: 'none', cursor: 'pointer', padding: 4,
              }}>
                <Icon name="close" size={20} style={{ color: C.t3 }} />
              </button>
            </div>
            <ConfigPanel config={config} setConfig={setConfig} onReset={resetConfig} mob={mob} />
          </div>
        </div>
      )}

      {/* ═════ Generate API Key Modal ═════ */}
      {keyModalOpen && (
        <div onClick={resetKeyModal} style={{
          position: 'fixed', inset: 0, background: 'rgba(30,24,18,.5)',
          backdropFilter: 'blur(4px)', zIndex: 9999,
          display: 'grid', placeItems: mob ? 'end center' : 'center', padding: mob ? 0 : 16,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: C.w, width: '100%', maxWidth: mob ? '100%' : 540,
            maxHeight: mob ? '92vh' : '88vh',
            borderRadius: mob ? '18px 18px 0 0' : 18,
            boxShadow: '0 24px 64px rgba(0,0,0,.2)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            {!generatedKey ? (
              /* ── Form view ── */
              <>
                {/* Header */}
                <div style={{ padding: mob ? '20px 16px 10px' : '28px 28px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <div>
                      <h2 style={{ fontSize: mob ? 18 : 20, fontWeight: 800 }}>Generate API Key</h2>
                      <p style={{ fontSize: 13, color: C.t2, marginTop: 4 }}>Set permissions for specialized extraction models.</p>
                    </div>
                    <button onClick={resetKeyModal} aria-label="Close" style={{
                      border: 'none', background: 'none', cursor: 'pointer', padding: 4, marginTop: -2,
                    }}>
                      <Icon name="close" size={22} style={{ color: C.t3 }} />
                    </button>
                  </div>
                </div>

                {/* Body — scrollable */}
                <div style={{ flex: 1, overflowY: 'auto', padding: mob ? '14px 16px 20px' : '16px 28px 24px' }}>
                  {/* Key Name */}
                  <div style={{ marginBottom: 22 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: C.t1, marginBottom: 8 }}>Key Name</label>
                    <input type="text" placeholder="e.g. Production Mobile App"
                      value={keyForm.name} onChange={e => setKeyForm(p => ({ ...p, name: e.target.value }))}
                      style={{
                        width: '100%', padding: '11px 14px', borderRadius: 10, fontSize: 13,
                        border: `1px solid ${C.bdr}`, outline: 'none', color: C.t1, boxSizing: 'border-box',
                        transition: 'border-color .15s',
                      }}
                      onFocus={e => e.target.style.borderColor = C.primary}
                      onBlur={e => e.target.style.borderColor = C.bdr}
                    />
                  </div>

                  {/* Environment */}
                  <div style={{ marginBottom: 22 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: C.t1, marginBottom: 8 }}>Environment</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      {['production', 'staging'].map(env => {
                        const active = keyForm.env === env;
                        return (
                          <button key={env} onClick={() => setKeyForm(p => ({ ...p, env }))} style={{
                            padding: '12px 0', borderRadius: 10, fontSize: 13, fontWeight: 600,
                            cursor: 'pointer', textTransform: 'capitalize',
                            border: `1.5px solid ${active ? C.primary : C.bdr}`,
                            background: active ? C.pL : C.w,
                            color: active ? C.primary : C.t1,
                            transition: 'all .15s',
                          }}>{env}</button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Capabilities */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <label style={{ fontSize: 13, fontWeight: 700, color: C.t1 }}>Capabilities</label>
                      <button onClick={selectAllCaps} style={{
                        border: 'none', background: 'none', fontSize: 12, fontWeight: 600,
                        color: C.primary, cursor: 'pointer',
                      }}>{keyForm.caps.length === KEY_CAPABILITIES.length ? 'Deselect All' : 'Select All'}</button>
                    </div>
                    <div style={{
                      display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr',
                      gap: '10px 24px', padding: mob ? 14 : 16, borderRadius: 12,
                      background: '#faf9f8', border: `1px solid ${C.bdr}`,
                    }}>
                      {KEY_CAPABILITIES.map(cap => {
                        const checked = keyForm.caps.includes(cap);
                        return (
                          <label key={cap} style={{
                            display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                            fontSize: 13, fontWeight: 500, color: C.t1,
                          }}>
                            <span onClick={() => toggleCap(cap)} style={{
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                              border: `1.5px solid ${checked ? C.primary : '#c4c0bc'}`,
                              background: checked ? C.primary : C.w,
                              cursor: 'pointer', transition: 'all .12s',
                            }}>
                              {checked && <Icon name="check" size={13} style={{ color: '#fff' }} />}
                            </span>
                            {cap}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div style={{
                  padding: mob ? '14px 16px' : '18px 28px',
                  background: '#faf9f8', borderTop: `1px solid ${C.bdr}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10,
                }}>
                  <button onClick={resetKeyModal} style={{
                    padding: '10px 20px', borderRadius: 10, border: `1px solid ${C.bdr}`,
                    background: C.w, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: C.t1,
                  }}>Cancel</button>
                  <button onClick={handleGenerateKey} disabled={!keyForm.name.trim()} style={{
                    padding: '10px 22px', borderRadius: 10, border: 'none',
                    background: !keyForm.name.trim() ? '#d6d3d1' : C.primary,
                    color: '#fff', fontSize: 13, fontWeight: 700,
                    cursor: keyForm.name.trim() ? 'pointer' : 'not-allowed',
                    boxShadow: keyForm.name.trim() ? '0 4px 14px rgba(236,91,19,.25)' : 'none',
                    transition: 'all .15s',
                  }}>Generate Key</button>
                </div>
              </>
            ) : (
              /* ── Success view ── */
              <>
                <div style={{ padding: mob ? 20 : 32, textAlign: 'center' }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: '50%', background: '#dcfce7',
                    display: 'grid', placeItems: 'center', margin: '0 auto 16px',
                  }}>
                    <Icon name="check" size={28} style={{ color: C.green }} />
                  </div>
                  <h3 style={{ fontSize: 18, fontWeight: 800, marginBottom: 6 }}>API Key Generated</h3>
                  <p style={{ fontSize: 12, color: C.t2, lineHeight: 1.5, marginBottom: 20 }}>
                    Copy your key now. It won't be shown again for security reasons.
                  </p>

                  {/* Key display */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '12px 14px', borderRadius: 10, background: '#1a1a1a',
                    border: '1px solid #333', marginBottom: 20,
                  }}>
                    <code style={{
                      flex: 1, fontSize: 12, color: '#34d399', fontFamily: "'Courier New', monospace",
                      wordBreak: 'break-all', lineHeight: 1.5, textAlign: 'left',
                    }}>{generatedKey}</code>
                    <button onClick={handleCopyKey} style={{
                      border: 'none', background: '#333', borderRadius: 8, padding: '6px 12px',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                      fontSize: 11, fontWeight: 700, color: keyCopied ? C.green : '#fff',
                      flexShrink: 0, transition: 'color .15s',
                    }}>
                      <Icon name={keyCopied ? 'check' : 'content_copy'} size={14} />
                      {keyCopied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>

                  {/* Summary */}
                  <div style={{
                    padding: 16, borderRadius: 10, background: '#faf9f8', border: `1px solid ${C.bdr}`,
                    textAlign: 'left',
                  }}>
                    {[
                      ['Name', keyForm.name],
                      ['Environment', keyForm.env],
                      ['Capabilities', `${keyForm.caps.length} selected`],
                    ].map(([k, v], i) => (
                      <div key={k} style={{
                        display: 'flex', justifyContent: 'space-between',
                        padding: '6px 0',
                        borderBottom: i < 2 ? `1px solid ${C.bdr}` : 'none',
                      }}>
                        <span style={{ fontSize: 11, color: C.t3 }}>{k}</span>
                        <span style={{ fontSize: 12, fontWeight: 600, textTransform: k === 'Environment' ? 'capitalize' : 'none' }}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{
                  padding: mob ? '14px 16px' : '18px 28px',
                  background: '#faf9f8', borderTop: `1px solid ${C.bdr}`,
                  display: 'flex', justifyContent: 'center',
                }}>
                  <button onClick={resetKeyModal} style={{
                    padding: '10px 32px', borderRadius: 10, border: 'none',
                    background: C.primary, color: '#fff', fontSize: 13, fontWeight: 700,
                    cursor: 'pointer', boxShadow: '0 4px 14px rgba(236,91,19,.25)',
                  }}>Done</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
